import type { FC, RefObject } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { LockClosedIcon } from '@heroicons/react/24/solid';
import cn from 'classnames';

import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { CommunityPost } from '~/interfaces/community';
import { NectarPageBlock, nectarPageBlock } from '~/utils/nectarPageBlock';

import { PillButton, pillButtonInteraction } from '../ui/PillButton';
import { DEFAULT_COLLAPSED_MAX_HEIGHT_CLASS } from '../ui/ReadMoreClamp';

import { useOpenChannelUpgradeModal } from '../../_hooks/useOpenChannelUpgradeModal';
import { useSpaceAccess } from '../../_hooks/useSpaceAccess';
import { useSpacePurchasableTiers } from '../../_hooks/useSpacePurchasableTiers';
import { TiptapRenderer } from '../TiptapRenderer';
import { PostAuthorHeader } from './_components/PostAuthorHeader';

interface GatedPostCardProps {
  post: CommunityPost;
  hideSpace?: boolean;
  isProfilePinned?: boolean;
  ref?: RefObject<HTMLElement | null>;
}

// Gated cards are blurred, unreadable placeholders with an unlock CTA on top.
export const GatedPostCard: FC<GatedPostCardProps> = ({ ref, post, hideSpace = false, isProfilePinned = false }) => {
  const { t } = useTranslation();
  const isLoggedIn = useIsLoggedIn();
  const space = post.space;
  const channelName = space?.name ?? '';
  const { hasPurchasableTier, isPending } = useSpacePurchasableTiers({
    spaceId: space?.id ?? '',
    enabled: isLoggedIn && space != null,
  });
  const { isSpaceConditionLocked } = useSpaceAccess();
  // Conditions take precedence over paid tiers, so a member who doesn't meet one has nothing to upgrade to.
  const isConditionLocked = space != null && isSpaceConditionLocked(space);

  // Guests (no account) have nothing to upgrade and already see the Join Community
  // banner at the top, so they get the blurred post without a CTA. Members wait
  // for the catalog so the CTA never appears and then vanishes.
  const showsGate = space != null && isLoggedIn && !isPending;

  return (
    <article
      ref={ref}
      aria-label={t('community.paid_gate.locked_post')}
      className="relative border border-black/10 rounded-3xl w-full"
    >
      <div aria-hidden="true" className="select-none pointer-events-none blur-[6px]">
        <div className="flex items-center gap-6 pt-4 px-4">
          <PostAuthorHeader
            author={post.author}
            createdAt={post.created_at}
            space={post.space}
            hideSpace={hideSpace}
            isPinned={isProfilePinned || (hideSpace && post.is_pinned)}
          />
        </div>

        <div className="flex flex-col gap-4 px-4 pt-4 pb-4">
          <h3 className="text-xl font-semibold leading-6 tracking-[-0.4px] text-black break-words">{post.title}</h3>
          <TiptapRenderer
            tiptapState={post.tiptap_state}
            className={`${DEFAULT_COLLAPSED_MAX_HEIGHT_CLASS} overflow-hidden`}
          />
        </div>
      </div>

      {showsGate && space ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-4">
          {hasPurchasableTier && !isConditionLocked ? (
            <UnlockChannelButton spaceId={space.id} channelName={channelName} />
          ) : (
            <p className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm text-black/60 shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]">
              <LockClosedIcon aria-hidden="true" className="size-4" />
              <Trans
                i18nKey={
                  isConditionLocked ? 'community.owner_gate.channel_locked' : 'community.paid_gate.channel_locked'
                }
                components={{ channel: <strong className="font-semibold text-black">{channelName}</strong> }}
              />
            </p>
          )}
        </div>
      ) : null}
    </article>
  );
};

interface UnlockChannelButtonProps {
  spaceId: string;
  channelName: string;
}

const UnlockChannelButton: FC<UnlockChannelButtonProps> = ({ spaceId, channelName }) => {
  const openChannelUpgradeModal = useOpenChannelUpgradeModal();

  return (
    <PillButton
      variant="dark"
      allowWrap
      onClick={() => openChannelUpgradeModal(spaceId, NectarPageBlock.CommunityFeedGatedPostsUpgradeButton)}
      className={cn(pillButtonInteraction, 'pointer-events-auto max-w-full gap-3')}
      {...nectarPageBlock(NectarPageBlock.CommunityFeedGatedPostsUpgradeButton)}
    >
      <LockClosedIcon aria-hidden="true" className="size-4 shrink-0" />
      {/* The channel name is a React child of the placeholder, not an i18n value:
          it never goes through interpolation escaping or HTML parsing. It never
          breaks mid-word: it drops whole onto the next line when it doesn't fit
          after the verb, and truncates only when it can't fit on a line of its own.
          Left-aligned so "Unlock" and the name start on the same column when wrapped. */}
      <span className="min-w-0 text-left">
        <Trans
          i18nKey="community.paid_gate.unlock_channel"
          components={{
            channel: (
              <strong className="inline-block max-w-full truncate align-bottom font-semibold">{channelName}</strong>
            ),
          }}
        />
      </span>
    </PillButton>
  );
};
